import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { inject, Injectable } from '@angular/core';
import { SortByDirection } from '@flexyoo/api';
import { TenantGroupDto } from '@flexyoo/api/models';
import {
  DataSourcePaginator,
  DataSourceState,
  DataSourceStateProvider,
  Pager,
} from '@flexyoo/shared/models';
import { TenantGroupHomeDataAccessService } from '@flexyoo/tenant-group/data-access';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

@Injectable()
export class TenantGroupsTableDataSourceService
  extends DataSource<TenantGroupDto>
  implements DataSourceStateProvider, DataSourcePaginator
{
  private readonly dataAccessService = inject(TenantGroupHomeDataAccessService);
  private readonly refresh$ = new BehaviorSubject<void>(null);
  private readonly pager$ = new BehaviorSubject<Pager>({
    pageIndex: 0,
    pageSize: 25,
  });

  public readonly loadingState$ = new BehaviorSubject<DataSourceState>('loading');
  public readonly state$ = this.loadingState$.pipe(distinctUntilChanged());

  private readonly result$ = combineLatest([this.pager$, this.refresh$]).pipe(
    tap(() => this.loadingState$.next('loading')),
    switchMap(([pager]) => this.dataAccessService.getTenantGroupsList(pager, SortByDirection.Asc)),
    tap(() => this.loadingState$.next('loaded')),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public readonly pagination$ = this.result$.pipe(
    withLatestFrom(this.pager$),
    map(([result, pager]) => ({
      length: result.TotalCount,
      pageIndex: result.CurrentPage,
      pageSize: pager.pageSize,
    }))
  );

  public setPager(pager: Pager): void {
    this.pager$.next(pager);
  }

  public reloadData(): void {
    this.refresh$.next(void 0);
  }

  connect(collectionViewer: CollectionViewer): Observable<readonly TenantGroupDto[]> {
    return this.result$.pipe(map((result) => result.Items ?? []));
  }

  disconnect(collectionViewer: CollectionViewer): void {}
}
